/*
 * Turns YouTube / Vimeo / direct MP4-WebM https links into embed markup. Used by the editor's "Add video" dialog,
 * when a link is pasted into the editor, and when a saved description is rendered (a bare link on its own line
 * becomes a player).
 */

const escapeAttribute = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')

const YOUTUBE_ID = /^[\w-]{11}$/

/** The `<video>` markup for an uploaded or directly linked MP4/WebM file. */
export const videoFileHtml = (url: string, type: string) =>
	`<video controls="controls" preload="metadata"><source src="${escapeAttribute(url)}" type="${type}"></video>`

/** YouTube refuses an embed with "Error 153" when the page sends no Referer, and the app is served with
 * `Referrer-Policy: no-referrer`; this per-iframe policy overrides that for the embed's own request. */
export const EMBED_REFERRER_POLICY = 'strict-origin-when-cross-origin'

const EMBED_ALLOW = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'

const iframeHtml = (src: string, title: string) =>
	`<iframe src="${src}" title="${title}" width="560" height="315" frameborder="0" allow="${EMBED_ALLOW}" referrerpolicy="${EMBED_REFERRER_POLICY}" allowfullscreen="allowfullscreen"></iframe>`

/** Embed markup for a pasted link, or null when it is not a YouTube/Vimeo/direct MP4-WebM https link. */
export function videoLinkToHtml(raw: string): string | null {
	let url: URL
	try {
		url = new URL(raw.trim())
	} catch {
		return null
	}
	if (url.protocol !== 'https:') return null

	const host = url.hostname.replace(/^(www|m)\./, '')

	let youtubeId: string | null | undefined
	if (host === 'youtu.be') youtubeId = url.pathname.split('/')[1]
	else if (host === 'youtube.com') {
		youtubeId = url.pathname === '/watch' ? url.searchParams.get('v') : /^\/(?:embed|shorts|live)\/([\w-]{11})/.exec(url.pathname)?.[1]
	}
	if (youtubeId && YOUTUBE_ID.test(youtubeId)) return iframeHtml(`https://www.youtube.com/embed/${youtubeId}`, 'YouTube video')

	const vimeoId = (host === 'vimeo.com' || host === 'player.vimeo.com') && /^\/(?:video\/)?(\d+)/.exec(url.pathname)?.[1]
	if (vimeoId) return iframeHtml(`https://player.vimeo.com/video/${vimeoId}`, 'Vimeo video')

	if (/\.mp4$/i.test(url.pathname)) return videoFileHtml(url.href, 'video/mp4')
	if (/\.webm$/i.test(url.pathname)) return videoFileHtml(url.href, 'video/webm')
	return null
}

const URL_LIKE = /^https:\/\/\S+$/i

/** The URL a paragraph consists of, when that is all it holds: a bare URL, or a link whose text is its address. */
function loneLink(paragraph: Element): string | null {
	const text = paragraph.textContent?.trim() ?? ''
	if (!URL_LIKE.test(text)) return null

	const anchors = paragraph.querySelectorAll('a')
	const breaks = paragraph.querySelectorAll('br')
	if (anchors.length > 1 || paragraph.children.length > anchors.length + breaks.length) return null
	// A link with its own text ("Watch the recap") is the author's wording; leave it a link.
	if (anchors.length === 1 && anchors[0].textContent?.trim() !== text) return null
	return anchors[0]?.getAttribute('href')?.trim() || text
}

/** Every paragraph under `root` that is only a YouTube/Vimeo/MP4-WebM link, with the embed it should become. */
export function findLoneLinkEmbeds(root: ParentNode): { paragraph: Element; embed: string }[] {
	const found: { paragraph: Element; embed: string }[] = []
	root.querySelectorAll('p').forEach(paragraph => {
		const link = loneLink(paragraph)
		const embed = link && videoLinkToHtml(link)
		if (embed) found.push({ paragraph, embed })
	})
	return found
}

/** Replaces each paragraph that is only a YouTube/Vimeo/MP4-WebM link with its embed. */
export function embedVideoLinks(html: string): string {
	if (!html.includes('https://')) return html

	const body = new DOMParser().parseFromString(html, 'text/html').body
	const embeds = findLoneLinkEmbeds(body)
	embeds.forEach(({ paragraph, embed }) => {
		paragraph.innerHTML = embed
	})
	return embeds.length ? body.innerHTML : html
}

/** The embed for pasted editor content that is only a video link (as plain text or markup); null otherwise. */
export function pastedVideoEmbed(pastedContent: string): string | null {
	const text = new DOMParser().parseFromString(pastedContent, 'text/html').body.textContent?.trim() ?? ''
	return URL_LIKE.test(text) ? videoLinkToHtml(text) : null
}
